// The one line of /mymaker (pages/MyMaker.js): a ground line that the
// pencil turns into things as the page is scrolled - a signature, the
// maker's canvas, a sound wave, a speech bubble, a laugh, the canvas the
// ways of making work on (components/MakerTools.js) - and in the end the
// board.
//
// Every piece is a run of relative path commands that starts and ends on the
// ground (y = GROUND), so the pieces can be strung into one unbroken line and
// the pencil never lifts: where a drawing needs to get somewhere inside
// itself, it goes back over a line it already drew. The page measures each
// piece in the browser, so nothing here needs its exact width.
//
// A piece can carry a `step`: [chapter, line] - the text that shows from the
// moment the pencil reaches it. Steps sit on the stretch of ground before a
// drawing, so the words arrive just before the thing they're about.
//
// A `pause` piece draws nothing: it is a stretch of scrolling where the
// pencil rests, so a finished drawing stays in view while its words are
// read.

export const GROUND = 250; // the drawing is 300 units tall
export const HEIGHT = 300;

const ground = (n) => `h ${n}`;
const pause = (n) => ({ pause: n });

// ---------------------------------------------------------------------------
// The drawings
// ---------------------------------------------------------------------------

const SIGNATURE =
  'c 10 -40 40 -70 60 -50 c 18 18 -20 50 -30 30 c -8 -18 30 -60 60 -40 ' +
  'c 20 14 0 40 10 50 c 12 10 40 -30 60 -20 c 14 8 10 22 30 30';

// The blank canvas, standing on the line.
const CANVAS = 'h 10 v -120 h 120 v 120 h 10';

// The sound of it: two notes joined by a beam, standing on the line - just
// the notes, drawn the way a hand draws them: the head a loop with smaller
// loops scribbled inside it, so it reads as filled in.
const NOTE_HEAD =
  'v -6 a 9 6 -15 1 1 -18 0 a 9 6 -15 1 1 18 0 h -3 a 6 4 -15 1 1 -12 0 a 6 4 -15 1 1 12 0 ' +
  'h -3 a 3 2 -15 1 1 -6 0 a 3 2 -15 1 1 6 0 h 6';
const SOUND = `h 30 ${NOTE_HEAD} v -50 l 40 -8 v 64 ${NOTE_HEAD} v 6 h 30`;

// Something they always say: a speech bubble.
const BUBBLE =
  'h 20 v -30 l 14 -20 h 70 q 14 0 14 -14 v -36 q 0 -14 -14 -14 h -100 ' +
  'q -14 0 -14 14 v 36 q 0 14 14 14 h 16 l 0 20 v 30 h 120';

// An inside joke: "haha", written by hand on the line. The h goes up its
// stem and comes down it again a little off, like a marker, then makes its
// arch; the a goes up its stem, round its belly and back down.
const LOWER_H = 'c 1 -20 2 -40 -2 -58 c -3 18 -1 38 2 58 c 2 -16 10 -30 22 -30 c 12 0 14 10 14 30';
const LOWER_A =
  'h 30 c 1 -12 1 -26 -2 -36 c -8 -6 -26 -4 -28 12 c -2 16 10 26 20 22 c 6 -2 9 -8 10 -14 c 0 6 1 12 3 16';
const LAUGH = `h 14 ${LOWER_H} h 8 ${LOWER_A} h 14 ${LOWER_H} h 8 ${LOWER_A} h 30`;

// The board: three by three. The middle spot is left empty here - the page
// puts the canvas the maker just made in it.
const BOARD = 'h 20 v -150 h 150 v 150 h -50 v -150 h -50 v 150 h -50 v -50 h 150 v -50 h -150 v 100 h 150';

// The maker's canvas, big, standing on the line: what the ways of making
// work on. Its inside starts 10 in from where the piece starts.
const WORK_CANVAS = 'h 10 v -150 h 150 v 150 h 10';
export const WORK_CANVAS_INSET = 10;
export const BOARD_SPOT = { x: 20 + 50, y: -100, size: 50 }; // from the board piece's start

// ---------------------------------------------------------------------------
// The line, in order
// ---------------------------------------------------------------------------

/** The part of the line you scroll through, up to the menu of ways of
 *  making: [{ d, step? } | { pause }]. Chapter 2 has one line of text per
 *  memory drawn: the sound, something they say, a laugh. */
export function buildScrollLine() {
  const MEMORIES = [SOUND, BUBBLE, LAUGH];

  const pieces = [
    { d: ground(30), step: [0, 0] },
    pause(120),
    { d: SIGNATURE },
    { d: ground(30) },
    pause(140),

    { d: ground(70), step: [1, 0] },
    { d: CANVAS },
    { d: ground(20) },
    pause(160),
    { d: ground(10), step: [1, 1] },
    pause(180),
    { d: ground(60) },
  ];

  MEMORIES.forEach((d, i) => {
    pieces.push({ d: ground(70), step: [2, i] }, { d }, { d: ground(30) }, pause(170));
  });

  // Into the menu: the pencil draws the canvas the ways of making work on,
  // then waits beside it.
  pieces.push({ d: ground(70), step: [3, 0] }, { d: WORK_CANVAS, canvas: true }, { d: ground(30) }, pause(60));
  return pieces;
}

/** The ending: the board, three by three, its middle spot for your canvas.
 *  The board is the second run. */
export function endingPieces() {
  // Stops just past the board, so all of it stays in view on a phone.
  return [ground(80), BOARD, ground(8)];
}
